import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ModalRutaMapa.scss';
import { extraerHora } from '../../../utilities/formateos';
import { getUnidadPos } from '../../../views/monitoreo/ServiciosEnVivo/integracion';
import markerImage from '../../../assets/icons/red_icon.png';
import markerStop from '../../../assets/icons/stop.png';
import busMarker from '../../../assets/icons/camion.png';

// ===== CONFIGURACIÓN =====
const CONFIG = {
    INTERVALO_ACTUALIZACION: 6000, // 6 segundos
    DELAY_LIMPIEZA: 300, // 300ms
    CENTRO_MAPA_DEFAULT: [20.6737, -103.3444], // Guadalajara
    ZOOM_DEFAULT: 13,
    ALTURA_MAPA: '500px',
};

const COLORES_POLYLINE = {
    PLANEADO: 'red',
    WIALON: '#20C3E5',
};

// ===== CONFIGURACIÓN DE ICONOS =====
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const crearIcono = (iconUrl, iconSize, iconAnchor, popupAnchor) => new L.Icon({
    iconUrl,
    iconSize,
    iconAnchor,
    popupAnchor,
});

const ICONOS = {
    STOP_WIALON: crearIcono(markerStop, [24, 24], [12, 24], [0, -24]),
    CAMION: crearIcono(busMarker, [50, 25], [16, 25], [0, -32]),
};

// ===== HELPER: Crear marcador personalizado con número =====
const crearMarkerPersonalizado = (marker, index) => {
    const ajusteLeft = index < 10 ? "17px" : "14.5px";

    return L.divIcon({
        className: "custom-marker",
        html: `
            <img style="width:40px;position:relative" src="${markerImage}"/>
            <div style="position:absolute;top:12px;left:${ajusteLeft};font-weight:bold;font-size:0.6rem">${index + 1}</div>
           
        `,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
    });
};

// ===== COMPONENTE PRINCIPAL =====
const ModalRutaMapa = ({ isOpen, onClose, rutaData, idBitacora }) => {
    // Estados
    const [trazoData, setTrazoData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [unidadPos, setUnidadPos] = useState(null);
    const [popupTrazoInfo, setPopupTrazoInfo] = useState(null);
    const intervalRef = useRef(null);
    const mapKeyRef = useRef(0);
    const centroInicialRef = useRef(null); // Guardar el centro inicial del mapa

    // ===== UTILIDADES =====
    const resetearEstados = () => {
        centroInicialRef.current = null;
        setTrazoData(null);
        setUnidadPos(null);
        setLoading(false);
        setPopupTrazoInfo(null);
        mapKeyRef.current += 1; // Incrementar key para nueva instancia del mapa
    };

    const limpiarIntervalo = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    // ===== FUNCIONES DE DATOS =====
    const cargarTrazo = async () => {
        if (!idBitacora) return;

        setLoading(true);
        try {
            const { getTrazoServiciosEnVivo } = await import('../../../views/monitoreo/ServiciosEnVivo/integracion');
            const response = await getTrazoServiciosEnVivo(idBitacora);
            setTrazoData(response.data || response);
        } catch (error) {
            console.error('Error al cargar trazo:', error);
        } finally {
            setLoading(false);
        }
    };

    const actualizarPosicionUnidad = async (idUnidad) => {
        try {
            const response = await getUnidadPos(idUnidad);
            setUnidadPos(response.data);
        } catch (error) {
            console.error('Error al obtener posición de unidad:', error);
        }
    };

    // ===== FUNCIONES PARA POPUP DEL TRAZO =====
    const getClosestPoint = (mouseLatLng, points) => {
        let minDist = Infinity;
        let closestPoint = null;
        for (let point of points) {
            const dist = mouseLatLng.distanceTo(L.latLng(point.lat, point.lng));
            if (dist < minDist) {
                minDist = dist;
                closestPoint = point;
            }
        }
        return closestPoint;
    };

    const handleTrazoClick = useCallback((e) => {
        const polylineWialon = trazoData?.TRAZO_WAILLON?.trazo;
        if (!polylineWialon || polylineWialon.length === 0) return;

        const mouseLatLng = e.latlng;
        const closest = getClosestPoint(mouseLatLng, polylineWialon);

        if (closest) {
            setPopupTrazoInfo({
                latlng: L.latLng(closest.lat, closest.lng),
                hora: closest.hora ? `Hora: ${closest.hora}` : "Hora no disponible",
                velocidad: closest.velocidad !== undefined ? `Velocidad: ${closest.velocidad} km/h` : "Velocidad no disponible",
            });
        }
    }, [trazoData]);

    const handleClosePopup = useCallback(() => {
        setPopupTrazoInfo(null);
    }, []);

    // ===== DATOS COMPUTADOS =====
    const estadoRuta = useMemo(() => {
        if (!trazoData) return null;

        return {
            iniciada: !!trazoData.HORA_INICIO_RUTA,
            finalizada: rutaData?.estatusId == 10 || rutaData?.estatusId == 11,
            enProceso: trazoData.TRAZO_WAILLON?.RUTA_EN_PROCESO || false,
            idUnidad: trazoData.ID_UNIDAD,
        };
    }, [trazoData, rutaData]);
    const datosDelMapa = useMemo(() => {
        if (!trazoData?.TRAZO) return null;

        const markers = trazoData.TRAZO.markers || [];
        const polylineRuta = trazoData.TRAZO.polyline?.trazo || [];
        const polylineWialon = trazoData.TRAZO_WAILLON?.trazo || [];
        const stops = trazoData.TRAZO_WAILLON?.stops || [];


        return { markers, polylineRuta, polylineWialon, stops };
    }, [trazoData]);
    const centroMapa = useMemo(() => {
        // Si ya tenemos un centro guardado, usarlo
        if (centroInicialRef.current) {
            return centroInicialRef.current;
        }
        
        if (!datosDelMapa) return CONFIG.CENTRO_MAPA_DEFAULT;
        
        let centro = CONFIG.CENTRO_MAPA_DEFAULT;
        
        // Priorizar: primera parada planeada, primer punto del trazo real, o primer punto del trazo planeado
        if (datosDelMapa.markers && datosDelMapa.markers.length > 0) {
            const primerMarker = datosDelMapa.markers[0];
            centro = [parseFloat(primerMarker.lat), parseFloat(primerMarker.lng)];
        } else if (datosDelMapa.polylineWialon && datosDelMapa.polylineWialon.length > 0) {
            const primerPunto = datosDelMapa.polylineWialon[0];
            centro = [primerPunto.lat, primerPunto.lng];
        } else if (datosDelMapa.polylineRuta && datosDelMapa.polylineRuta.length > 0) {
            const primerPunto = datosDelMapa.polylineRuta[0];
            centro = [primerPunto.lat, primerPunto.lng];
        }
        
        // Guardar el centro para futuras actualizaciones
        centroInicialRef.current = centro;
        return centro
        
        return CONFIG.CENTRO_MAPA_DEFAULT;
    }, [datosDelMapa]);


    // ===== EFECTOS =====
    // Efecto 1: Limpieza al cerrar el modal
    useEffect(() => {
        if (!isOpen) {
            limpiarIntervalo();
            const timeoutId = setTimeout(resetearEstados, CONFIG.DELAY_LIMPIEZA);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    // Efecto 2: Carga inicial de datos
    useEffect(() => {
        if (isOpen && idBitacora) {
            resetearEstados();
            cargarTrazo();
        }
    }, [isOpen, idBitacora]);

    // Efecto 3: Actualización en tiempo real
    useEffect(() => {
        // const debeActualizar = isOpen &&
        //     estadoRuta?.enProceso &&
        //     estadoRuta?.idUnidad &&
        //     !estadoRuta?.finalizada;
        const debeActualizar = isOpen &&
            estadoRuta?.idUnidad 

        if (debeActualizar) {
            actualizarPosicionUnidad(estadoRuta.idUnidad);

            intervalRef.current = setInterval(async () => {
                await cargarTrazo();
                await actualizarPosicionUnidad(estadoRuta.idUnidad);
            }, CONFIG.INTERVALO_ACTUALIZACION);
        }

        return limpiarIntervalo;
    }, [isOpen, estadoRuta?.enProceso, estadoRuta?.idUnidad, estadoRuta?.finalizada]);

    // ===== MAPA MEMOIZADO =====
    const Mapa = useMemo(() => {
        if (!trazoData) return null;

        return (
            <MapContainer
                center={centroMapa}
                zoom={CONFIG.ZOOM_DEFAULT}
                style={{ height: CONFIG.ALTURA_MAPA, width: '100%' }}
                className="mapa-ruta"
            >
                {/* <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                /> */}
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CARTO'
                    url={'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_272a_1_35c523a1879227e1480394f9'}
                    errorTileUrl="https://upload.wikimedia.org/wikipedia/commons/e/e0/Blank.png"
                />
                {/* Paradas planeadas */}
                {datosDelMapa?.markers?.map((marker, index) => (
                    <Marker
                        key={marker.ID || index}
                        position={[parseFloat(marker.lat), parseFloat(marker.lng)]}
                        icon={crearMarkerPersonalizado(marker, index)}
                    >
                        <Popup>
                            <div className="popup-marker">
                                <strong>{marker.NOMBRE}</strong>
                                <p>Horario: {marker.HORARIO}</p>
                                <p>Orden: {marker.ORDEN + 1}</p>
                                {marker.REFERENCIA && <p><em>{marker.REFERENCIA}</em></p>}
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Stops de Wialon */}
                {datosDelMapa?.stops?.map((stop, index) => (
                    <Marker
                        key={`stop-${index}`}
                        position={[parseFloat(stop.lat), parseFloat(stop.lng)]}
                        icon={ICONOS.STOP_WIALON}
                    >
                        <Popup>
                            <div className="popup-stop">
                                <strong>🛑 STOP</strong>
                                <p>Hora: {stop.Hora || stop.hora}</p>
                                <p>Velocidad: {stop.velocidad} km/h</p>
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Polílínea roja - Trazo planeado */}
                {datosDelMapa?.polylineRuta && datosDelMapa.polylineRuta.length > 0 && (
                    <Polyline
                        positions={datosDelMapa.polylineRuta.map(p => [p.lat, p.lng])}
                        color={COLORES_POLYLINE.PLANEADO}
                        weight={7}
                        opacity={1}
                    />
                )}

                {/* Polílínea azul - Trazo real de Wialon */}
                {datosDelMapa?.polylineWialon && datosDelMapa.polylineWialon.length > 0 && (
                    <Polyline
                        positions={datosDelMapa.polylineWialon.map(p => [p.lat, p.lng])}
                        color={COLORES_POLYLINE.WIALON}
                        weight={6}
                        opacity={0.7}
                        eventHandlers={{
                            click: handleTrazoClick,
                        }}
                    />
                )}

                {/* Popup del trazo */}
                {popupTrazoInfo && (
                    <Popup
                        key={`popup-${popupTrazoInfo.latlng.lat}-${popupTrazoInfo.latlng.lng}`}
                        position={popupTrazoInfo.latlng}
                        autoPan={false}
                        closeButton={true}
                        onClose={handleClosePopup}
                    >
                        <div className="popup-trazo">
                            <div style={{ fontWeight: "bold" }}>{popupTrazoInfo.hora}</div>
                            <div style={{ fontWeight: "bold" }}>{popupTrazoInfo.velocidad}</div>
                        </div>
                    </Popup>
                )}

                {/* Camión en tiempo real */}
                {unidadPos && (
                    <Marker
                        position={[unidadPos.lat, unidadPos.lng]}
                        icon={ICONOS.CAMION}
                        zIndexOffset={1000}
                        className="marker-camion"

                    >
                        <Popup>
                            <div className="popup-camion">
                                <strong>🚌 Camión en ruta</strong>
                                <p>Operador: {trazoData?.CHOFER}</p>
                                {trazoData?.ID_UNIDAD && <p>Unidad: {trazoData.ID_UNIDAD}</p>}
                            </div>
                        </Popup>
                    </Marker>
                )}
            </MapContainer>
        );
    }, [trazoData, datosDelMapa, unidadPos, handleTrazoClick, popupTrazoInfo, handleClosePopup, centroMapa]); // Dependencias necesarias

    // ===== RENDERIZADO CONDICIONAL =====
    if (!isOpen) return null;

    // ===== RENDER PRINCIPAL =====
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <div>
                        <h2>{rutaData?.nombre || 'Detalle de Ruta'}</h2>
                        {trazoData && (
                            <div className="info-basica">
                                {trazoData.ID_BITACORA && (
                                    <span><strong>Bitácora:</strong> {trazoData.ID_BITACORA}</span>
                                )}
                                <span><strong>Operador:</strong> {trazoData.CHOFER}</span>
                                {trazoData.ID_UNIDAD && (
                                    <span><strong>Unidad:</strong> {trazoData.ID_UNIDAD}</span>
                                )}
                                {trazoData.PASAJEROS.length > 0 && (
                                    <span><strong>Pasajeros:</strong> {trazoData.PASAJEROS.length}</span>
                                )}
                                {trazoData.HORA_ARRANQUE && (
                                    <span><strong>Arranque:</strong> {extraerHora(trazoData.HORA_ARRANQUE)}</span>
                                )}
                                {trazoData.HORA_LLEGADA && (
                                    <span><strong>Llegada estimada:</strong> {extraerHora(trazoData.HORA_LLEGADA)}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <button className="btn-cerrar" onClick={onClose}>✕</button>
                </div>

                {/* Estado de la ruta */}
                {estadoRuta && (
                    <>
                        {!estadoRuta.iniciada && (
                            <div className="estado-mensaje no-iniciada">
                                <span className="icono">⏳</span>
                                <span>La ruta aún no ha iniciado</span>
                            </div>
                        )}
                        {estadoRuta.finalizada && (
                            <div className="estado-mensaje terminada">
                                <span className="icono">✅</span>
                                <span>Ruta completada</span>
                            </div>
                        )}
                        {estadoRuta.enProceso && !estadoRuta.finalizada && (
                            <div className="estado-mensaje en-proceso">
                                <span className="icono pulse">🚌</span>
                                <span>Ruta en proceso - Actualización en tiempo real</span>
                            </div>
                        )}
                    </>
                )}

                {/* Cuerpo del modal - Mapa */}
                <div className="modal-body">
                    {loading && !trazoData ? (
                        <div className="loading">
                            <span className="spinner">⏳</span>
                            <p>Cargando información de la ruta...</p>
                        </div>
                    ) : (
                        Mapa
                    )}
                </div>

                {/* Footer - Leyenda */}
                {datosDelMapa && (
                    <div className="modal-footer">
                        <div className="leyenda">
                            <div className="leyenda-item">
                                <div className="linea roja"></div>
                                <span>Trazo planeado</span>
                            </div>
                            <div className="leyenda-item">
                                <div className="linea azul"></div>
                                <span>Trazo real (Wialon)</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModalRutaMapa;
